/**
 * The two halves of a visit: what was consumed, and what is owed.
 *
 * §9.2 hangs charges off a *check*, and a check hangs off the visit that opened
 * it — which is how it reaches a table at all. Both halves are written once here
 * rather than inside the dialog that mounts them, because "what was consumed"
 * and "what is owed" are the same two questions wherever a visit is opened from.
 *
 * The reads are keyed by `?sessionId=`, which both `GET /orders` and
 * `GET /checks` accept. They used to accept `?partyId=` as well, and the second
 * mount that used it — a party with no table at all — went with Party: a bill
 * with no visit behind it is a walk-up now and belongs on the till's list.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ORDER_ITEM_TRANSITIONS, canTransition } from '@/lib/domain';
import type { OrderItemStatus } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { rowsOf } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { EmptyState, ErrorState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import CheckDetail from '@/Pages/Billing/CheckDetail';
import type { Check, CashierShift } from '@/Pages/Billing/lib';
import { apiMessage } from '../lib/helpers';

export interface OrderItemRow {
  id: string;
  name: string;
  quantity: string;
  unitPrice: string;
  total: string;
  status: string;
  notes: string | null;
  /** A component chosen inside a meal (§6.2) — priced within its parent. */
  parentItemId: string | null;
  modifiers?: { id: string; name: string }[];
}

export interface OrderRow {
  id: string;
  number: string;
  status: string;
  total: string;
  items?: OrderItemRow[];
}

/**
 * The sum of the visit's orders, in the branch currency's own minor units, so
 * the figure matches what the orders actually say. `null` when there are none.
 */
export function ordersTotalOf(orders: OrderRow[] | undefined, decimals: number): string | null {
  if (!orders?.length) return null;
  const scale = 10 ** decimals;
  const minor = orders.reduce((sum, order) => sum + Math.round(Number(order.total) * scale), 0);
  return (minor / scale).toFixed(decimals);
}

// ---------------------------------------------------------------- products

/**
 * Everything the visit has consumed, across every order of it.
 *
 * A meal's components are priced inside their parent (§6.2), so counting them
 * as lines of their own would double the total. They are listed under the
 * parent instead, which is also how the kitchen and the bill read them.
 */
export function ConsumedProducts({
  orders,
  branchId,
  emptyHint,
  onChanged,
}: {
  orders: OrderRow[];
  branchId: string | null;
  /** "…at this table" is wrong for a visit that has no table. */
  emptyHint?: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { format } = useCurrency(branchId);
  const confirmer = useConfirm();
  const [acting, setActing] = useState<string | null>(null);

  const consumed = useMemo(() => {
    const rows = orders.flatMap((order) =>
      (order.items ?? []).map((item) => ({
        ...item,
        orderId: order.id,
        orderNumber: order.number,
        orderStatus: order.status,
      })),
    );
    const parents = rows.filter((row) => !row.parentItemId);
    const childrenOf = new Map<string, typeof rows>();
    for (const row of rows) {
      if (!row.parentItemId) continue;
      childrenOf.set(row.parentItemId, [...(childrenOf.get(row.parentItemId) ?? []), row]);
    }
    return { parents, childrenOf };
  }, [orders]);

  const run = useCallback(
    async (key: string, work: () => Promise<unknown>, success: string) => {
      setActing(key);
      try {
        await work();
        toast.success(success);
        onChanged();
      } catch (error) {
        toast.error(apiMessage(error, t('operations.errors.actionFailed')));
      } finally {
        setActing(null);
      }
    },
    [onChanged, t],
  );

  const cancelLine = useCallback(
    async (orderId: string, item: { id: string; name: string }) => {
      // A line already sent has consumed stock and posted a charge, so the
      // server voids both — that is a correction, and a correction needs a
      // reason on the audit trail.
      const reason = await confirmer.prompt({
        title: t('spaces.session.cancelLineTitle', { name: item.name }),
        body: t('spaces.session.cancelReason', { name: item.name }),
        label: t('spaces.session.reason'),
        confirmLabel: t('spaces.session.cancelLine'),
        tone: 'danger',
      });
      if (reason === null) return;
      void run(
        item.id,
        () =>
          api.delete(`/orders/${orderId}/items/${item.id}`, {
            data: { reason: reason.trim() || undefined },
          }),
        t('spaces.session.lineCancelled'),
      );
    },
    [confirmer, run, t],
  );

  const changeQuantity = useCallback(
    async (orderId: string, item: { id: string; name: string; quantity: string }) => {
      const next = await confirmer.prompt({
        title: t('spaces.session.quantityFor', { name: item.name }),
        label: t('spaces.session.quantity'),
        initial: String(Number(item.quantity)),
        validate: (input) =>
          Number.isFinite(Number(input)) && Number(input) > 0
            ? null
            : t('spaces.session.quantityInvalid'),
      });
      if (next === null) return;
      const quantity = Number(next);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        toast.error(t('spaces.session.quantityInvalid'));
        return;
      }
      void run(
        item.id,
        () => api.patch(`/orders/${orderId}/items/${item.id}`, { quantity }),
        t('spaces.session.lineUpdated'),
      );
    },
    [confirmer, run, t],
  );

  return (
    <>
      {/* An order that never reached the kitchen is the other thing that can be
          acted on here, and it is invisible on a per-line view. `POST
          /orders/:id/send` charges for `orders.send`, so a user without it is
          not shown a button that would 403. */}
      {can('orders.send') &&
        orders
          .filter((order) => order.status === 'DRAFT' || order.status === 'CONFIRMED')
          .map((order) => (
            <div
              key={order.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--border)] p-3 text-sm"
            >
              <span>{t('spaces.session.notSent', { number: order.number })}</span>
              <Button
                size="sm"
                disabled={acting === order.id}
                onClick={() =>
                  void run(
                    order.id,
                    () => api.post(`/orders/${order.id}/send`, {}),
                    t('spaces.session.sent'),
                  )
                }
              >
                {t('spaces.session.sendToKitchen')}
              </Button>
            </div>
          ))}

      {!consumed.parents.length ? (
        <EmptyState
          title={t('spaces.session.noOrders')}
          hint={emptyHint ?? t('spaces.session.noOrdersHint')}
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('spaces.session.product')}</TH>
                <TH className="text-end">{t('spaces.session.qty')}</TH>
                <TH>{t('spaces.live.state')}</TH>
                <TH>{t('spaces.session.order')}</TH>
                <TH className="text-end">{t('spaces.session.total')}</TH>
                <TH />
              </TR>
            </THead>
            <tbody>
              {consumed.parents.map((item) => (
                <TR key={item.id}>
                  <TD>
                    <span className="font-medium">{item.name}</span>
                    {!!item.modifiers?.length && (
                      <span className="block text-xs text-ink-muted">
                        {item.modifiers.map((modifier) => modifier.name).join(' · ')}
                      </span>
                    )}
                    {consumed.childrenOf.get(item.id)?.map((child) => (
                      <span key={child.id} className="block text-xs text-ink-muted">
                        + {child.name}
                      </span>
                    ))}
                    {!!item.notes && (
                      <span className="block text-xs italic text-ink-muted">
                        {item.notes}
                      </span>
                    )}
                  </TD>
                  <TD className="text-end tabular-nums">{Number(item.quantity)}</TD>
                  <TD>
                    <Badge tone={toneForStatus(item.status)}>
                      {t(`operations.itemStatus.${item.status}`, item.status)}
                    </Badge>
                  </TD>
                  <TD className="text-xs text-ink-muted">{item.orderNumber}</TD>
                  <TD className="text-end tabular-nums">{format(item.total)}</TD>
                  <TD className="text-end">
                    {can('orders.update') && (
                      <div className="flex justify-end gap-1">
                        {/* Quantity is only editable before the kitchen has the
                            line; after that the correction is a cancel, which
                            voids the charge and returns the stock. */}
                        {item.status === 'PENDING' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={acting === item.id}
                            onClick={() => changeQuantity(item.orderId, item)}
                          >
                            {t('spaces.session.changeQty')}
                          </Button>
                        )}
                        {canTransition(
                          ORDER_ITEM_TRANSITIONS,
                          item.status as OrderItemStatus,
                          'CANCELLED',
                        ) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={acting === item.id}
                            onClick={() => cancelLine(item.orderId, item)}
                          >
                            {t('spaces.session.cancelLine')}
                          </Button>
                        )}
                      </div>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </>
  );
}

// -------------------------------------------------------------------- bill

export interface VisitChecks {
  checks: Check[];
  shift: CashierShift | null;
  checkId: string | null;
  setCheckId: (id: string) => void;
  error: string | null;
  reload: () => void;
  /** What the still-open bills add up to, or `null` when nothing is owed. */
  outstanding: string | null;
}

/**
 * The bills of one visit. `params` is `{ sessionId }` — `QueryChecksDto`
 * declares it, and `forbidNonWhitelisted` means anything else would be a 400.
 */
export function useVisitChecks(
  params: Record<string, string> | null,
  branchId: string | null,
): VisitChecks {
  const { t } = useTranslation();
  const { decimals } = useCurrency(branchId);
  const [checks, setChecks] = useState<Check[]>([]);
  const [shift, setShift] = useState<CashierShift | null>(null);
  const [checkId, setCheckId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The object identity changes every render; the ids inside it do not.
  const key = params ? JSON.stringify(params) : null;

  const reload = useCallback(() => {
    if (!key) return;
    setError(null);
    api
      .get('/checks', { params: { ...(JSON.parse(key) as Record<string, string>), limit: 50 } })
      .then(({ data }) => {
        const rows = rowsOf<Check>(data).rows;
        setChecks(rows);
        setCheckId((current) =>
          current && rows.some((row) => row.id === current)
            ? current
            : // Open on the bill still owing, not merely the first one.
              (rows.find((row) => row.status === 'OPEN' || row.status === 'PARTIALLY_PAID')?.id ??
              rows[0]?.id ??
              null),
        );
      })
      .catch((e) => setError(apiMessage(e, t('spaces.checks.loadFailed'))));
  }, [key, t]);

  useEffect(reload, [reload]);

  // A payment needs an open till, and the shift belongs to the visit's branch.
  useEffect(() => {
    if (!key || !branchId) return;
    api
      .get<CashierShift | null>('/cashier-shifts/current', { params: { branchId } })
      .then(({ data }) => setShift(data ?? null))
      .catch(() => setShift(null));
  }, [key, branchId]);

  const outstanding = useMemo(() => {
    const live = checks.filter(
      (check) => check.status === 'OPEN' || check.status === 'PARTIALLY_PAID',
    );
    if (!live.length) return null;
    // Minor units, so the figure matches the printed bill exactly.
    const scale = 10 ** decimals;
    const minor = live.reduce((sum, check) => sum + Math.round(Number(check.grandTotal) * scale), 0);
    return (minor / scale).toFixed(decimals);
  }, [checks, decimals]);

  return { checks, shift, checkId, setCheckId, error, reload, outstanding };
}

export function BillPane({
  bill,
  branchId,
  emptyHint,
  onChanged,
}: {
  bill: VisitChecks;
  branchId: string | null;
  emptyHint?: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency(branchId);

  if (bill.error) return <ErrorState message={bill.error} onRetry={bill.reload} />;
  if (!bill.checks.length)
    return (
      <EmptyState title={t('spaces.checks.none')} hint={emptyHint ?? t('spaces.checks.noneHint')} />
    );

  return (
    <>
      {/* A split visit carries several bills, so they are tabs. */}
      {bill.checks.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {bill.checks.map((check) => (
            <button
              key={check.id}
              type="button"
              onClick={() => bill.setCheckId(check.id)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                check.id === bill.checkId
                  ? 'border-[var(--primary)] bg-[var(--muted)]'
                  : 'border-[var(--border)]'
              }`}
            >
              <span className="font-medium">{check.number}</span>
              <Badge tone={toneForStatus(check.status)}>
                {t(`billing.status.${check.status}`, check.status)}
              </Badge>
              <span className="tabular-nums">{format(check.grandTotal)}</span>
            </button>
          ))}
        </div>
      )}

      {/* Split, move, merge, pay and refund are `CheckDetail`'s, so no screen
          that shows a bill can drift from the till. */}
      {bill.checkId && (
        <CheckDetail
          key={bill.checkId}
          checkId={bill.checkId}
          checks={bill.checks}
          shift={bill.shift}
          onChanged={onChanged}
        />
      )}
    </>
  );
}

/** The products/bill switch, identical on both dialogs. */
export type VisitPane = 'products' | 'bill';

export function PaneTabs({
  pane,
  onPick,
}: {
  pane: VisitPane;
  onPick: (pane: VisitPane) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 border-b border-[var(--border)] pb-2">
      {(['products', 'bill'] as const).map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => onPick(key)}
          className={`rounded-lg px-4 py-2 text-sm ${
            pane === key
              ? 'bg-accent-solid font-medium text-accent-on'
              : 'text-ink-muted hover:text-[var(--foreground)]'
          }`}
        >
          {t(`spaces.session.pane.${key}`)}
        </button>
      ))}
    </div>
  );
}

/** One labelled figure in a dialog's header strip. */
export function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-ink-muted">{label}</p>
      <p className="mt-0.5 text-sm font-medium">{value}</p>
    </div>
  );
}
